<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
	import { ref } from "vue";
	import ButtonBack from "./ButtonBack.vue";
	import ButtonPrimary from "./ButtonPrimary.vue";

	interface DialogProps {
		title: string;
		showCancel?: boolean;
		cancelText?: string;
		confirmText?: string;
		loading?: boolean;
	}

	withDefaults(defineProps<DialogProps>(), {
		showCancel: true,
		cancelText: "Cancel",
		confirmText: "Confirm",
		loading: false,
	});

	const emit = defineEmits<{
		cancel: [];
		confirm: [];
	}>();

	const dialogRef = ref<HTMLDialogElement | null>(null);

	function show() {
		dialogRef.value?.showModal();
	}

	function hide() {
		dialogRef.value?.close();
	}

	function handleOverlayClick(e: MouseEvent) {
		if (e.target === dialogRef.value) {
			emit("cancel");
			hide();
		}
	}

	function handleCancel() {
		emit("cancel");
		hide();
	}

	function handleConfirm() {
		emit("confirm");
	}

	defineExpose({
		show,
		hide,
	});
</script>

<template>
	<Teleport to="body">
		<dialog
			ref="dialogRef"
			class="rounded-2xl max-w-md min-w-[400px] p-0 shadow-xl"
			@click="handleOverlayClick"
		>
			<div class="bg-white rounded-2xl">
				<div
					class="flex items-center justify-between px-6 py-4 bg-green-50 border-b border-green-200 rounded-t-2xl"
				>
					<h3 class="text-lg font-semibold title text-green-800">
						{{ title }}
					</h3>
					<button
						@click="handleCancel"
						class="text-grey-400 hover:text-grey-600 p-1"
					>
						<svg
							class="!w-6 !h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							></path>
						</svg>
					</button>
				</div>
				<div class="px-6 pb-6 pt-4">
					<div class="mb-4">
						<slot />
					</div>
					<div class="flex gap-3">
						<ButtonBack
							v-if="showCancel"
							type="button"
							@click="handleCancel"
							class="flex-1 px-4 py-2"
							:disabled="loading"
						>
							{{ cancelText }}
						</ButtonBack>
						<ButtonPrimary
							type="button"
							@click="handleConfirm"
							class="flex-1 px-4 py-2 font-medium"
							:disabled="loading"
						>
							<span
								v-if="loading"
								class="flex items-center justify-center"
							>
								<svg
									class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
								Loading...
							</span>
							<span v-else>{{ confirmText }}</span>
						</ButtonPrimary>
					</div>
				</div>
			</div>
		</dialog>
	</Teleport>
</template>
